import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/Home.css";
import heroBoardEn from "../assets/hero-board-182-en.jpg";
import heroBoardPl from "../assets/hero-board-182-pl.jpg";
import heroBoardDe from "../assets/hero-board-182-de.jpg";
import featureMissions from "../assets/feature-missions-182.jpg";
import featureFlashcards from "../assets/feature-flashcards-182.jpg";

const STORE_APPLE = "https://apps.apple.com/pl/app/dialecto/id6746904195";
const STORE_GOOGLE = "https://play.google.com/store/apps/details?id=com.dialectoapp&hl=en";

// SEO landing pages target the language their queries are searched in:
// EN for "learn european portuguese" / "learn norwegian", PL for "nauka norweskiego".
const MN_ALTERNATES = [
  { hrefLang: "en", href: "https://dialectoapp.com/mnemonics-app/" },
  { hrefLang: "pl", href: "https://dialectoapp.com/mnemotechniki/" },
  { hrefLang: "x-default", href: "https://dialectoapp.com/mnemonics-app/" },
];

const NO_ALTERNATES = [
  { hrefLang: "en", href: "https://dialectoapp.com/learn-norwegian/" },
  { hrefLang: "pl", href: "https://dialectoapp.com/nauka-norweskiego/" },
  { hrefLang: "de", href: "https://dialectoapp.com/norwegisch-lernen/" },
  { hrefLang: "uk", href: "https://dialectoapp.com/vyvchyty-norvezku/" },
  { hrefLang: "x-default", href: "https://dialectoapp.com/learn-norwegian/" },
];

const PT_ALTERNATES = [
  { hrefLang: "en", href: "https://dialectoapp.com/learn-european-portuguese/" },
  { hrefLang: "fr", href: "https://dialectoapp.com/apprendre-le-portugais-europeen/" },
  { hrefLang: "it", href: "https://dialectoapp.com/imparare-il-portoghese-europeo/" },
  { hrefLang: "es", href: "https://dialectoapp.com/aprender-portugues-europeo/" },
  { hrefLang: "de", href: "https://dialectoapp.com/europaeisches-portugiesisch-lernen/" },
  { hrefLang: "x-default", href: "https://dialectoapp.com/learn-european-portuguese/" },
];

const PAGES = {
  pt: {
    slug: "learn-european-portuguese",
    alternates: PT_ALTERNATES,
    title: "Learn European Portuguese — App for the Portuguese of Portugal | Dialecto",
    description:
      "Learn European Portuguese with Dialecto — the Portuguese spoken in Portugal, not Brazil, from beginner level. Role-play real situations, remember words with smart mnemonics, and prepare for your trip. Free on iOS and Android.",
    eyebrow: "European Portuguese",
    h1: "Learn European Portuguese — Not Brazilian",
    lead:
      "Most apps teach Brazilian Portuguese. Dialecto is a learning app for the Portuguese actually spoken in Lisbon, Porto, and the Algarve — the pronunciation, the vocabulary, and the everyday phrases you'll really hear in Portugal. Built for beginners.",
    boardAlt: "Dialecto flashcard teaching the European Portuguese word 'a mala' with a mnemonic",
    whyTitle: "Why European Portuguese is different",
    why: [
      {
        title: "Different pronunciation",
        desc: "European Portuguese swallows unstressed vowels and sounds sharper than Brazilian. Train your ear on the real thing from day one instead of relearning later.",
      },
      {
        title: "Different everyday words",
        desc: "Comboio not trem, pequeno-almoço not café da manhã, telemóvel not celular. Dialecto teaches the words people in Portugal actually use.",
      },
      {
        title: "Formal and informal done right",
        desc: "Tu vs. você works differently in Portugal than in Brazil. Practice conversations that get the register right, so you don't sound off.",
      },
    ],
    howTitle: "How you'll learn European Portuguese with Dialecto",
    how: [
      "Role-play real situations — order a bica in a Lisbon café, check in at a hotel in Porto, chat with locals at the market.",
      "Remember words for good with mnemonic flashcards — every word gets a memory trick and a picture, spaced repetition brings it back right before you'd forget.",
      "Prepare for your trip — tell Dialecto your travel plans and get practice missions built around the exact conversations you'll have in Portugal.",
      "Learn with Portuguese music — current hit playlists from Portugal, one tap away on Spotify, Apple Music, or YouTube.",
      "Start from zero — everything is built for beginner level, with guidance in English.",
    ],
    faq: [
      {
        q: "Does Dialecto teach European or Brazilian Portuguese?",
        a: "European Portuguese only — the variant spoken in Portugal. Vocabulary, phrases, and conversation scenarios all follow European usage, so what you learn matches what you'll hear in Lisbon or Porto.",
      },
      {
        q: "Can I start as a complete beginner?",
        a: "Yes. Dialecto is designed for beginners: you start with the most useful everyday words and phrases, and the app adapts the difficulty as you progress.",
      },
      {
        q: "Is Dialecto free?",
        a: "You can download Dialecto free on iOS and Android. Premium unlocks unlimited practice and starts with a 3-day free trial — cancel anytime.",
      },
      {
        q: "How fast can I learn enough for a trip to Portugal?",
        a: "With the trip missions feature, you practice exactly the conversations your trip will involve — most learners feel ready for café, restaurant, and small-talk situations within a few weeks of regular practice.",
      },
    ],
    ctaTitle: "Start speaking European Portuguese",
    ctaSubtitle: "Download Dialecto free and have your first Portuguese conversation today.",
  },
  "pt-fr": {
    slug: "apprendre-le-portugais-europeen",
    lang: "fr",
    ogLocale: "fr_FR",
    alternates: PT_ALTERNATES,
    ui: "fr",
    title: "Apprendre le portugais européen — l'app du portugais du Portugal | Dialecto",
    description:
      "Apprends le portugais européen avec Dialecto — celui parlé au Portugal, pas au Brésil, dès le niveau débutant. Joue de vraies situations, mémorise le vocabulaire avec des astuces mnémotechniques et prépare ton voyage. Gratuit sur iOS et Android.",
    eyebrow: "Portugais européen",
    h1: "Apprendre le portugais européen — pas le brésilien",
    lead:
      "La plupart des applis enseignent le portugais brésilien. Dialecto est une app pour apprendre le portugais réellement parlé à Lisbonne, Porto et en Algarve — la prononciation, le vocabulaire et les phrases du quotidien que tu entendras vraiment au Portugal. Conçue pour les débutants.",
    boardAlt: "Fiche Dialecto enseignant le mot de portugais européen « a mala » avec une astuce mnémotechnique",
    whyTitle: "Pourquoi le portugais européen est différent",
    why: [
      {
        title: "Une prononciation différente",
        desc: "Le portugais européen avale les voyelles atones et sonne plus sec que le brésilien. Habitue ton oreille au vrai portugais dès le départ, plutôt que de tout réapprendre ensuite.",
      },
      {
        title: "Des mots du quotidien différents",
        desc: "Comboio et non trem, pequeno-almoço et non café da manhã, telemóvel et non celular. Dialecto enseigne les mots que les gens utilisent vraiment au Portugal.",
      },
      {
        title: "Le tu et le vous, comme au Portugal",
        desc: "Tu et você ne fonctionnent pas comme au Brésil. Entraîne-toi sur des conversations qui emploient le bon registre, pour ne pas sonner faux.",
      },
    ],
    howTitle: "Comment tu vas apprendre le portugais européen avec Dialecto",
    how: [
      "Joue de vraies situations — commande une bica dans un café de Lisbonne, fais ton check-in à l'hôtel à Porto, discute avec les habitants au marché.",
      "Mémorise le vocabulaire pour de bon avec des fiches mnémotechniques — chaque mot reçoit une astuce mémoire et une image, et la répétition espacée le fait revenir juste avant que tu l'oublies.",
      "Prépare ton voyage — indique tes projets à Dialecto et reçois des missions d'entraînement construites autour des conversations exactes que tu auras au Portugal.",
      "Apprends avec la musique portugaise — les playlists des tubes du moment au Portugal, à un tap sur Spotify, Apple Music ou YouTube.",
      "Commence de zéro — tout est conçu pour le niveau débutant.",
    ],
    faq: [
      {
        q: "Dialecto enseigne-t-il le portugais européen ou brésilien ?",
        a: "Uniquement le portugais européen — la variante parlée au Portugal. Le vocabulaire, les phrases et les scénarios de conversation suivent l'usage européen, donc ce que tu apprends correspond à ce que tu entendras à Lisbonne ou à Porto.",
      },
      {
        q: "Puis-je commencer en grand débutant ?",
        a: "Oui. Dialecto est conçu pour les débutants : tu commences par les mots et phrases les plus utiles du quotidien, et l'app adapte la difficulté à mesure que tu progresses.",
      },
      {
        q: "Dialecto est-il gratuit ?",
        a: "Tu peux télécharger Dialecto gratuitement sur iOS et Android. Premium débloque l'entraînement illimité et commence par un essai gratuit de 3 jours — annulable à tout moment.",
      },
      {
        q: "En combien de temps puis-je apprendre assez pour un voyage au Portugal ?",
        a: "Avec les missions de voyage, tu t'entraînes exactement sur les conversations que ton séjour impliquera — la plupart des apprenants se sentent prêts pour le café, le restaurant et les échanges courants après quelques semaines de pratique régulière.",
      },
    ],
    ctaTitle: "Commence à parler le portugais européen",
    ctaSubtitle: "Télécharge Dialecto gratuitement et tiens ta première conversation en portugais dès aujourd'hui.",
  },
  "pt-it": {
    slug: "imparare-il-portoghese-europeo",
    lang: "it",
    ogLocale: "it_IT",
    alternates: PT_ALTERNATES,
    ui: "it",
    title: "Imparare il portoghese europeo — l'app del portoghese del Portogallo | Dialecto",
    description:
      "Impara il portoghese europeo con Dialecto — quello parlato in Portogallo, non in Brasile, dal livello principiante. Gioca situazioni reali, memorizza le parole con mnemotecniche e preparati al viaggio. Gratis su iOS e Android.",
    eyebrow: "Portoghese europeo",
    h1: "Imparare il portoghese europeo — non quello brasiliano",
    lead:
      "La maggior parte delle app insegna il portoghese brasiliano. Dialecto è un'app per imparare il portoghese davvero parlato a Lisbona, Porto e in Algarve — la pronuncia, il lessico e le frasi di tutti i giorni che sentirai davvero in Portogallo. Pensata per principianti.",
    boardAlt: "Flashcard Dialecto che insegna la parola del portoghese europeo « a mala » con una mnemotecnica",
    whyTitle: "Perché il portoghese europeo è diverso",
    why: [
      {
        title: "Una pronuncia diversa",
        desc: "Il portoghese europeo mangia le vocali atone e suona più netto del brasiliano. Abitua l'orecchio a quello vero fin dal primo giorno, invece di dover ricominciare dopo.",
      },
      {
        title: "Parole quotidiane diverse",
        desc: "Comboio e non trem, pequeno-almoço e non café da manhã, telemóvel e non celular. Dialecto insegna le parole che in Portogallo si usano davvero.",
      },
      {
        title: "Il tu e il lei, come in Portogallo",
        desc: "Tu e você funzionano diversamente rispetto al Brasile. Allenati su conversazioni che usano il registro giusto, per non suonare fuori posto.",
      },
    ],
    howTitle: "Come imparerai il portoghese europeo con Dialecto",
    how: [
      "Gioca situazioni reali — ordina una bica in un caffè di Lisbona, fai il check-in in hotel a Porto, chiacchiera con la gente al mercato.",
      "Memorizza le parole per sempre con flashcard mnemoniche — ogni parola riceve un trucco di memoria e un'immagine, e la ripetizione dilazionata la riporta poco prima che tu la dimentichi.",
      "Preparati al viaggio — indica i tuoi piani a Dialecto e ricevi missioni di pratica costruite sulle conversazioni esatte che avrai in Portogallo.",
      "Impara con la musica portoghese — le playlist dei successi del momento dal Portogallo, a un tocco su Spotify, Apple Music o YouTube.",
      "Parti da zero — tutto è pensato per il livello principiante.",
    ],
    faq: [
      {
        q: "Dialecto insegna il portoghese europeo o brasiliano?",
        a: "Solo il portoghese europeo — la variante parlata in Portogallo. Lessico, frasi e scenari di conversazione seguono l'uso europeo, così quello che impari corrisponde a ciò che sentirai a Lisbona o Porto.",
      },
      {
        q: "Posso iniziare da principiante assoluto?",
        a: "Sì. Dialecto è pensato per i principianti: inizi dalle parole e frasi più utili di tutti i giorni e l'app adatta la difficoltà man mano che progredisci.",
      },
      {
        q: "Dialecto è gratis?",
        a: "Puoi scaricare Dialecto gratis su iOS e Android. Premium sblocca la pratica illimitata e inizia con una prova gratuita di 3 giorni — disdici quando vuoi.",
      },
      {
        q: "In quanto tempo imparo abbastanza per un viaggio in Portogallo?",
        a: "Con le missioni di viaggio ti alleni esattamente sulle conversazioni che il tuo viaggio comporterà — la maggior parte di chi impara si sente pronta per caffè, ristorante e chiacchiere quotidiane dopo qualche settimana di pratica regolare.",
      },
    ],
    ctaTitle: "Inizia a parlare il portoghese europeo",
    ctaSubtitle: "Scarica Dialecto gratis e fai la tua prima conversazione in portoghese oggi stesso.",
  },
  "pt-es": {
    slug: "aprender-portugues-europeo",
    lang: "es",
    ogLocale: "es_ES",
    alternates: PT_ALTERNATES,
    ui: "es",
    title: "Aprender portugués europeo — la app del portugués de Portugal | Dialecto",
    description:
      "Aprende portugués europeo con Dialecto — el que se habla en Portugal, no en Brasil, desde nivel principiante. Practica situaciones reales, memoriza vocabulario con mnemotecnias y prepárate para tu viaje. Gratis en iOS y Android.",
    eyebrow: "Portugués europeo",
    h1: "Aprender portugués europeo — no brasileño",
    lead:
      "La mayoría de las apps enseñan portugués brasileño. Dialecto es una app para aprender el portugués que de verdad se habla en Lisboa, Oporto y el Algarve — la pronunciación, el vocabulario y las frases del día a día que oirás realmente en Portugal. Pensada para principiantes.",
    boardAlt: "Tarjeta de Dialecto que enseña la palabra del portugués europeo « a mala » con una mnemotecnia",
    whyTitle: "Por qué el portugués europeo es diferente",
    why: [
      {
        title: "Una pronunciación diferente",
        desc: "El portugués europeo se come las vocales átonas y suena más seco que el brasileño. Acostumbra el oído al de verdad desde el primer día, en lugar de reaprenderlo después.",
      },
      {
        title: "Palabras cotidianas diferentes",
        desc: "Comboio y no trem, pequeno-almoço y no café da manhã, telemóvel y no celular. Dialecto enseña las palabras que en Portugal se usan de verdad.",
      },
      {
        title: "El tú y el usted, como en Portugal",
        desc: "Tu y você funcionan distinto que en Brasil. Practica conversaciones con el registro correcto para no sonar raro.",
      },
    ],
    howTitle: "Cómo aprenderás portugués europeo con Dialecto",
    how: [
      "Practica situaciones reales — pide una bica en una cafetería de Lisboa, haz el check-in del hotel en Oporto, charla con la gente en el mercado.",
      "Memoriza el vocabulario para siempre con tarjetas mnemotécnicas — cada palabra recibe un truco de memoria y una imagen, y la repetición espaciada la trae de vuelta justo antes de que la olvides.",
      "Prepárate para tu viaje — cuéntale a Dialecto tus planes y recibe misiones de práctica creadas en torno a las conversaciones exactas que tendrás en Portugal.",
      "Aprende con música portuguesa — las playlists de los éxitos del momento en Portugal, a un toque en Spotify, Apple Music o YouTube.",
      "Empieza desde cero — todo está pensado para el nivel principiante.",
    ],
    faq: [
      {
        q: "¿Dialecto enseña portugués europeo o brasileño?",
        a: "Solo portugués europeo — la variante que se habla en Portugal. El vocabulario, las frases y los escenarios de conversación siguen el uso europeo, así que lo que aprendes coincide con lo que oirás en Lisboa u Oporto.",
      },
      {
        q: "¿Puedo empezar siendo principiante total?",
        a: "Sí. Dialecto está diseñado para principiantes: empiezas por las palabras y frases más útiles del día a día, y la app adapta la dificultad según avanzas.",
      },
      {
        q: "¿Dialecto es gratis?",
        a: "Puedes descargar Dialecto gratis en iOS y Android. Premium desbloquea la práctica ilimitada y empieza con una prueba gratuita de 3 días — cancela cuando quieras.",
      },
      {
        q: "¿En cuánto tiempo aprendo lo suficiente para un viaje a Portugal?",
        a: "Con las misiones de viaje practicas exactamente las conversaciones que tendrá tu viaje — la mayoría de estudiantes se siente lista para cafetería, restaurante y charlas cotidianas tras unas semanas de práctica regular.",
      },
    ],
    ctaTitle: "Empieza a hablar portugués europeo",
    ctaSubtitle: "Descarga Dialecto gratis y ten tu primera conversación en portugués hoy mismo.",
  },
  "pt-de": {
    slug: "europaeisches-portugiesisch-lernen",
    lang: "de",
    ogLocale: "de_DE",
    alternates: PT_ALTERNATES,
    ui: "de",
    title: "Europäisches Portugiesisch lernen — die App für das Portugiesisch Portugals | Dialecto",
    description:
      "Lerne europäisches Portugiesisch mit Dialecto — das in Portugal gesprochene, nicht das brasilianische, ab Anfängerniveau. Spiele echte Situationen durch, merke dir Wörter mit Eselsbrücken und bereite dich auf deine Reise vor. Kostenlos auf iOS und Android.",
    eyebrow: "Europäisches Portugiesisch",
    h1: "Europäisches Portugiesisch lernen — nicht brasilianisches",
    lead:
      "Die meisten Apps lehren brasilianisches Portugiesisch. Dialecto ist eine App zum Lernen des Portugiesisch, das wirklich in Lissabon, Porto und der Algarve gesprochen wird — die Aussprache, der Wortschatz und die Alltagssätze, die du in Portugal tatsächlich hörst. Für Anfänger gemacht.",
    boardAlt: "Dialecto-Karteikarte mit dem europäisch-portugiesischen Wort « a mala » und einer Eselsbrücke",
    whyTitle: "Warum europäisches Portugiesisch anders ist",
    why: [
      {
        title: "Eine andere Aussprache",
        desc: "Europäisches Portugiesisch verschluckt unbetonte Vokale und klingt schärfer als brasilianisches. Gewöhne dein Ohr vom ersten Tag an das Echte, statt später umzulernen.",
      },
      {
        title: "Andere Alltagswörter",
        desc: "Comboio statt trem, pequeno-almoço statt café da manhã, telemóvel statt celular. Dialecto lehrt die Wörter, die man in Portugal wirklich benutzt.",
      },
      {
        title: "Duzen und Siezen wie in Portugal",
        desc: "Tu und você funktionieren anders als in Brasilien. Übe Gespräche mit dem richtigen Register, damit du nicht daneben klingst.",
      },
    ],
    howTitle: "So lernst du europäisches Portugiesisch mit Dialecto",
    how: [
      "Spiele echte Situationen durch — bestell eine bica in einem Café in Lissabon, check im Hotel in Porto ein, plausch mit Einheimischen auf dem Markt.",
      "Merke dir Wörter dauerhaft mit Eselsbrücken-Karteikarten — jedes Wort bekommt einen Merktrick und ein Bild, und die verteilte Wiederholung bringt es zurück, kurz bevor du es vergisst.",
      "Bereite dich auf deine Reise vor — nenn Dialecto deine Pläne und erhalte Übungsmissionen rund um die Gespräche, die du in Portugal genau haben wirst.",
      "Lerne mit portugiesischer Musik — aktuelle Hit-Playlists aus Portugal, einen Tipp entfernt auf Spotify, Apple Music oder YouTube.",
      "Starte bei null — alles ist für Anfänger gebaut.",
    ],
    faq: [
      {
        q: "Lehrt Dialecto europäisches oder brasilianisches Portugiesisch?",
        a: "Nur europäisches Portugiesisch — die in Portugal gesprochene Variante. Wortschatz, Sätze und Gesprächsszenen folgen dem europäischen Gebrauch, sodass das Gelernte zu dem passt, was du in Lissabon oder Porto hörst.",
      },
      {
        q: "Kann ich als kompletter Anfänger starten?",
        a: "Ja. Dialecto ist für Anfänger gemacht: Du beginnst mit den nützlichsten Alltagswörtern und -sätzen, und die App passt den Schwierigkeitsgrad deinem Fortschritt an.",
      },
      {
        q: "Ist Dialecto kostenlos?",
        a: "Du kannst Dialecto kostenlos auf iOS und Android laden. Premium schaltet unbegrenztes Üben frei und startet mit einer 3-tägigen kostenlosen Testphase — jederzeit kündbar.",
      },
      {
        q: "Wie schnell lerne ich genug für eine Reise nach Portugal?",
        a: "Mit den Reise-Missionen übst du genau die Gespräche, die deine Reise mit sich bringt — die meisten Lernenden fühlen sich nach einigen Wochen regelmäßiger Übung für Café-, Restaurant- und Small-Talk-Situationen bereit.",
      },
    ],
    ctaTitle: "Fang an, europäisches Portugiesisch zu sprechen",
    ctaSubtitle: "Lade Dialecto kostenlos und führe noch heute dein erstes Gespräch auf Portugiesisch.",
  },
  no: {
    slug: "learn-norwegian",
    alternates: NO_ALTERNATES,
    title: "Norwegian Learning App for Beginners — Learn Bokmål | Dialecto",
    description:
      "Dialecto is a Norwegian learning app for beginners. Learn Bokmål through real conversations, remember words with smart mnemonics, and prepare for your trip or move to Norway. Free on iOS and Android.",
    eyebrow: "Norwegian Bokmål",
    h1: "Learn Norwegian for Beginners",
    lead:
      "Dialecto is a Norwegian learning app teaching Bokmål — the written standard used by the vast majority of Norwegians — through real conversations, clever memory tricks, and practice built around your own plans. No prior knowledge needed.",
    boardAlt: "Dialecto flashcard app teaching vocabulary with a mnemonic memory trick",
    whyTitle: "Why learn Norwegian with Dialecto",
    why: [
      {
        title: "Bokmål, the practical choice",
        desc: "Bokmål is the written form used by around 85% of Norwegians and what you'll see in Oslo, in newspapers, and at work. It's the standard courses and employers expect.",
      },
      {
        title: "Speak from day one",
        desc: "Norwegian grammar is friendlier than you think — no verb conjugation by person. Dialecto gets you into real conversations early instead of drilling tables.",
      },
      {
        title: "Words that stick",
        desc: "Every flashcard comes with a mnemonic and a picture, and spaced repetition resurfaces words right before you'd forget them — so vocabulary sticks for good.",
      },
    ],
    howTitle: "How you'll learn Norwegian with Dialecto",
    how: [
      "Role-play everyday situations — order coffee in Oslo, make small talk at work, handle a job interview in Norwegian.",
      "Remember words for good with mnemonic flashcards and spaced repetition.",
      "Prepare for your move or trip — tell Dialecto your plans and get practice missions built around the conversations you'll actually have in Norway.",
      "Learn with Norwegian music — current hit playlists from Norway, one tap away on Spotify, Apple Music, or YouTube.",
      "Start from zero — everything is built for beginner level, with guidance in English.",
    ],
    faq: [
      {
        q: "Does Dialecto teach Bokmål or Nynorsk?",
        a: "Bokmål — the written standard used by roughly 85% of Norwegians and the one nearly all learners start with. Once you know Bokmål, reading Nynorsk becomes much easier.",
      },
      {
        q: "Is Norwegian hard to learn for English speakers?",
        a: "Norwegian is considered one of the easiest languages for English speakers: related vocabulary, simple verb forms, and familiar word order. The challenge is practice — which is exactly what Dialecto gives you.",
      },
      {
        q: "Can I start as a complete beginner?",
        a: "Yes. Dialecto starts you with the most useful everyday words and phrases and adapts as you improve.",
      },
      {
        q: "Is Dialecto free?",
        a: "You can download Dialecto free on iOS and Android. Premium unlocks unlimited practice and starts with a 3-day free trial — cancel anytime.",
      },
    ],
    ctaTitle: "Start speaking Norwegian",
    ctaSubtitle: "Download Dialecto free and have your first Norwegian conversation today.",
  },
  "no-pl": {
    slug: "nauka-norweskiego",
    lang: "pl",
    ogLocale: "pl_PL",
    alternates: NO_ALTERNATES,
    ui: "pl",
    title: "Nauka norweskiego — aplikacja do nauki od podstaw (bokmål) | Dialecto",
    description:
      "Nauka norweskiego z aplikacją Dialecto — bokmål od podstaw. Scenki z życia, fiszki z mnemotechnikami i przygotowanie do wyjazdu lub pracy w Norwegii. Pobierz za darmo na iOS i Androida.",
    eyebrow: "Norweski bokmål",
    h1: "Nauka norweskiego od podstaw — aplikacja Dialecto",
    lead:
      "Dialecto to aplikacja do nauki norweskiego bokmål — standardu, którym posługuje się zdecydowana większość Norwegów. Uczysz się przez prawdziwe rozmowy, sprytne mnemotechniki i praktykę zbudowaną wokół twoich planów. Praca w Norwegii, wyjazd czy rodzina — zaczynasz od zera i mówisz od pierwszego dnia.",
    boardAlt: "Fiszka Dialecto z mnemotechniką — zapamiętaj słówka na zawsze",
    whyTitle: "Dlaczego warto uczyć się norweskiego z Dialecto",
    why: [
      {
        title: "Bokmål — praktyczny wybór",
        desc: "Bokmål to standard pisany używany przez około 85% Norwegów — w Oslo, w gazetach i w pracy. To jego oczekują pracodawcy i kursy.",
      },
      {
        title: "Mów od pierwszego dnia",
        desc: "Norweska gramatyka jest prostsza, niż myślisz — czasowniki nie odmieniają się przez osoby. Dialecto od razu wciąga cię w prawdziwe rozmowy zamiast wkuwania tabelek.",
      },
      {
        title: "Słówka, które zostają",
        desc: "Każda fiszka ma mnemotechnikę i obrazek, a powtórki wracają dokładnie wtedy, gdy zaczynasz zapominać — słownictwo zostaje na dobre.",
      },
    ],
    howTitle: "Jak nauczysz się norweskiego z Dialecto",
    how: [
      "Odgrywaj codzienne scenki — zamów kawę w Oslo, pogadaj ze współpracownikami, przejdź rozmowę kwalifikacyjną po norwesku.",
      "Zapamiętuj słówka na zawsze dzięki fiszkom z mnemotechnikami i powtórkom rozłożonym w czasie.",
      "Przygotuj się do wyjazdu lub przeprowadzki — podaj Dialecto swoje plany, a dostaniesz misje treningowe wokół rozmów, które naprawdę cię czekają w Norwegii.",
      "Ucz się z norweską muzyką — aktualne playlisty hitów z Norwegii, jedno tapnięcie do Spotify, Apple Music lub YouTube.",
      "Zacznij od zera — wszystko zbudowane pod poziom początkujący.",
    ],
    faq: [
      {
        q: "Bokmål czy nynorsk — czego uczy Dialecto?",
        a: "Bokmål — standardu pisanego używanego przez około 85% Norwegów, od którego zaczynają praktycznie wszyscy uczący się. Znając bokmål, bez trudu przeczytasz też nynorsk.",
      },
      {
        q: "Czy norweski jest trudny dla Polaków?",
        a: "Norweski uchodzi za jeden z łatwiejszych języków germańskich: czasowniki nie odmieniają się przez osoby, szyk zdania jest przewidywalny, a wiele słów przypomina angielskie i niemieckie. Kluczem jest regularna praktyka mówienia — i dokładnie to daje Dialecto.",
      },
      {
        q: "Czy nauczę się norweskiego do pracy w Norwegii?",
        a: "Tak — scenki obejmują sytuacje zawodowe: rozmowę kwalifikacyjną, small talk ze współpracownikami, kontakt z klientem. Możesz też stworzyć własną scenkę pod swoją branżę.",
      },
      {
        q: "Czy mogę zacząć zupełnie od zera?",
        a: "Tak. Dialecto zaczyna od najpotrzebniejszych codziennych słów i zwrotów, a poziom trudności dostosowuje się do twoich postępów.",
      },
      {
        q: "Ile kosztuje Dialecto?",
        a: "Aplikację pobierzesz za darmo na iOS i Androida. Premium odblokowuje nieograniczoną praktykę i zaczyna się od 3-dniowego darmowego okresu próbnego — możesz anulować w każdej chwili.",
      },
    ],
    ctaTitle: "Zacznij mówić po norwesku",
    ctaSubtitle: "Pobierz Dialecto za darmo i odbądź pierwszą rozmowę po norwesku już dziś.",
  },
  "no-de": {
    slug: "norwegisch-lernen",
    lang: "de",
    ogLocale: "de_DE",
    alternates: NO_ALTERNATES,
    ui: "de",
    title: "Norwegisch lernen — App für Anfänger (Bokmål) | Dialecto",
    description:
      "Norwegisch lernen mit Dialecto — Bokmål für Anfänger. Echte Gesprächsszenen, Karteikarten mit Eselsbrücken und Vorbereitung auf Reise oder Job in Norwegen. Kostenlos auf iOS und Android.",
    eyebrow: "Norwegisch Bokmål",
    h1: "Norwegisch lernen für Anfänger",
    lead:
      "Dialecto ist eine App zum Norwegisch lernen — Bokmål, der Schriftstandard, den die große Mehrheit der Norweger verwendet. Du lernst durch echte Gespräche, clevere Eselsbrücken und Übungen rund um deine eigenen Pläne. Ob Job in Norwegen, Reise oder Umzug — du startest bei null und sprichst vom ersten Tag an.",
    boardAlt: "Dialecto-Karteikarte mit einer Eselsbrücke — Wörter dauerhaft merken",
    whyTitle: "Warum Norwegisch lernen mit Dialecto",
    why: [
      {
        title: "Bokmål — die praktische Wahl",
        desc: "Bokmål ist die Schriftform, die rund 85 % der Norweger nutzen — in Oslo, in Zeitungen und im Beruf. Genau das erwarten Arbeitgeber und Kurse.",
      },
      {
        title: "Sprich ab dem ersten Tag",
        desc: "Norwegische Grammatik ist einfacher als gedacht — Verben werden nicht nach Person gebeugt. Dialecto bringt dich früh in echte Gespräche, statt Tabellen zu pauken.",
      },
      {
        title: "Wörter, die haften bleiben",
        desc: "Jede Karteikarte hat eine Eselsbrücke und ein Bild, und die Wiederholungen kommen genau dann zurück, wenn du zu vergessen beginnst — so bleibt der Wortschatz dauerhaft.",
      },
    ],
    howTitle: "So lernst du Norwegisch mit Dialecto",
    how: [
      "Spiele Alltagssituationen durch — bestell einen Kaffee in Oslo, mach Small Talk mit Kollegen, meistere ein Vorstellungsgespräch auf Norwegisch.",
      "Merke dir Wörter dauerhaft mit Eselsbrücken-Karteikarten und zeitlich verteilten Wiederholungen.",
      "Bereite dich auf Umzug oder Reise vor — nenn Dialecto deine Pläne und erhalte Übungsmissionen rund um die Gespräche, die dich in Norwegen wirklich erwarten.",
      "Lerne mit norwegischer Musik — aktuelle Hit-Playlists aus Norwegen, einen Tipp entfernt auf Spotify, Apple Music oder YouTube.",
      "Starte bei null — alles ist für Anfänger gebaut.",
    ],
    faq: [
      {
        q: "Bokmål oder Nynorsk — was lehrt Dialecto?",
        a: "Bokmål — den Schriftstandard, den rund 85 % der Norweger verwenden und mit dem praktisch alle Lernenden anfangen. Wer Bokmål kann, liest auch Nynorsk mühelos.",
      },
      {
        q: "Ist Norwegisch schwer für Deutschsprachige?",
        a: "Norwegisch gilt als eine der leichtesten Sprachen für Deutschsprachige: verwandter Wortschatz, einfache Verbformen und vertraute Satzstellung. Der Schlüssel ist regelmäßiges Sprechen — genau das gibt dir Dialecto.",
      },
      {
        q: "Lerne ich genug Norwegisch für den Job in Norwegen?",
        a: "Ja — die Szenen decken berufliche Situationen ab: Vorstellungsgespräch, Small Talk mit Kollegen, Kundenkontakt. Du kannst auch eigene Szenen für deine Branche erstellen.",
      },
      {
        q: "Kann ich als kompletter Anfänger starten?",
        a: "Ja. Dialecto beginnt mit den nützlichsten Alltagswörtern und -sätzen und passt den Schwierigkeitsgrad an deinen Fortschritt an.",
      },
      {
        q: "Was kostet Dialecto?",
        a: "Die App lädst du kostenlos auf iOS und Android. Premium schaltet unbegrenztes Üben frei und startet mit einer 3-tägigen kostenlosen Testphase — jederzeit kündbar.",
      },
    ],
    ctaTitle: "Fang an, Norwegisch zu sprechen",
    ctaSubtitle: "Lade Dialecto kostenlos und führe noch heute dein erstes Gespräch auf Norwegisch.",
  },
  "no-uk": {
    slug: "vyvchyty-norvezku",
    lang: "uk",
    ogLocale: "uk_UA",
    alternates: NO_ALTERNATES,
    ui: "uk",
    title: "Вивчити норвезьку — застосунок для початківців (букмол) | Dialecto",
    description:
      "Вивчення норвезької із застосунком Dialecto — букмол з нуля. Реальні діалоги, картки з мнемонікою та підготовка до поїздки чи роботи в Норвегії. Завантажте безкоштовно на iOS та Android.",
    eyebrow: "Норвезька (букмол)",
    h1: "Вивчити норвезьку для початківців",
    lead:
      "Dialecto — це застосунок для вивчення норвезької мови (букмол), писемного стандарту, яким користується переважна більшість норвежців. Ти вчишся через справжні розмови, дотепну мнемоніку та практику, побудовану навколо твоїх планів. Робота в Норвегії, переїзд чи поїздка — починаєш з нуля й говориш із першого дня.",
    boardAlt: "Картка Dialecto з мнемонікою — запам'ятай слова назавжди",
    whyTitle: "Чому варто вчити норвезьку з Dialecto",
    why: [
      {
        title: "Букмол — практичний вибір",
        desc: "Букмол — це писемна форма, якою користуються близько 85% норвежців: в Осло, у газетах і на роботі. Саме його очікують роботодавці та курси.",
      },
      {
        title: "Говори з першого дня",
        desc: "Норвезька граматика простіша, ніж здається — дієслова не змінюються за особами. Dialecto одразу залучає тебе до справжніх розмов замість зубріння таблиць.",
      },
      {
        title: "Слова, що залишаються",
        desc: "Кожна картка має мнемоніку та зображення, а повторення повертаються саме тоді, коли ти починаєш забувати — словниковий запас лишається надовго.",
      },
    ],
    howTitle: "Як ти вивчиш норвезьку з Dialecto",
    how: [
      "Розігруй щоденні ситуації — замов каву в Осло, поспілкуйся з колегами, пройди співбесіду норвезькою.",
      "Запам'ятовуй слова назавжди завдяки карткам із мнемонікою та повторенням з інтервалами.",
      "Готуйся до переїзду чи поїздки — розкажи Dialecto свої плани й отримай тренувальні місії навколо розмов, які справді чекають на тебе в Норвегії.",
      "Вчися з норвезькою музикою — актуальні плейлисти хітів із Норвегії, один дотик до Spotify, Apple Music чи YouTube.",
      "Починай з нуля — усе створено для рівня початківця.",
    ],
    faq: [
      {
        q: "Букмол чи нюношк — чого навчає Dialecto?",
        a: "Букмол — писемний стандарт, яким користуються близько 85% норвежців і з якого починають майже всі, хто вчить мову. Знаючи букмол, ти легко читатимеш і нюношк.",
      },
      {
        q: "Чи важко вивчити норвезьку?",
        a: "Норвезька вважається однією з найлегших германських мов: дієслова не змінюються за особами, порядок слів передбачуваний, а багато слів схожі на англійські та німецькі. Головне — регулярна практика говоріння, і саме це дає Dialecto.",
      },
      {
        q: "Чи вивчу я норвезьку для роботи в Норвегії?",
        a: "Так — сценки охоплюють робочі ситуації: співбесіду, невимушену розмову з колегами, спілкування з клієнтом. Ти також можеш створити власну сценку під свою галузь.",
      },
      {
        q: "Чи можу я почати повністю з нуля?",
        a: "Так. Dialecto починає з найпотрібніших щоденних слів і фраз, а рівень складності підлаштовується під твій прогрес.",
      },
      {
        q: "Скільки коштує Dialecto?",
        a: "Застосунок можна завантажити безкоштовно на iOS та Android. Premium відкриває необмежену практику й починається з 3-денного безкоштовного пробного періоду — скасувати можна будь-коли.",
      },
    ],
    ctaTitle: "Почни говорити норвезькою",
    ctaSubtitle: "Завантаж Dialecto безкоштовно й проведи першу розмову норвезькою вже сьогодні.",
  },
  mn: {
    slug: "mnemonics-app",
    alternates: MN_ALTERNATES,
    sideImg: "flashcards",
    title: "Mnemonics App for Language Learning — Remember Words Forever | Dialecto",
    description:
      "Dialecto is a mnemonics app for learning languages: every flashcard comes with a vivid memory trick and a picture, backed by spaced repetition. Stop forgetting vocabulary. Free on iOS and Android.",
    eyebrow: "Mnemonic method",
    h1: "The Mnemonics App for Language Learning",
    lead:
      "Forgetting words is normal — learning them twice is optional. In Dialecto every flashcard comes with a mnemonic: a vivid memory trick and a picture that link the word's sound to its meaning. Spaced repetition then brings it back right before you'd forget. Seven languages, including European Portuguese and Norwegian.",
    boardAlt: "Dialecto flashcard with a mnemonic memory trick — remember words forever",
    whyTitle: "Why mnemonics beat rote memorization",
    why: [
      {
        title: "Your brain loves stories",
        desc: "A word tied to a vivid image or mini-story leaves a far stronger memory trace than repeating it twenty times. Mnemonics turn abstract sounds into pictures you can't unsee.",
      },
      {
        title: "Ready for every word",
        desc: "You don't have to invent tricks yourself: Dialecto builds a tailored mnemonic with an illustration for any word you're learning — including ones you pick up in your own conversations.",
      },
      {
        title: "Spaced repetition seals it",
        desc: "Mnemonics get words into memory; spaced repetition keeps them there. Each card resurfaces right before you'd forget it, so reviews stay short and effective.",
      },
    ],
    howTitle: "How learning with mnemonics works in Dialecto",
    how: [
      "Meet a word in a real conversation — in role-play scenes, trip missions, or song lyrics.",
      "Tap it to make a flashcard — Dialecto creates a mnemonic and a picture linking the sound to the meaning.",
      "Review in short sessions — spaced repetition schedules every card at the perfect moment.",
      "Use the word back in conversation — recalling it under real conditions locks it in for good.",
      "Works across all seven languages — from European Portuguese to Norwegian.",
    ],
    faq: [
      {
        q: "What is a mnemonic, exactly?",
        a: "A memory trick that ties a new word to something you already know — an image, a story, a similar-sounding word. Example: Portuguese 'a mala' (suitcase) — picture your friend Mala dragging a giant suitcase through the airport. Silly on purpose: the sillier the image, the better it sticks.",
      },
      {
        q: "Do mnemonics really work for language learning?",
        a: "Yes — vivid associations and testing yourself at spaced intervals are two of the best-documented techniques in memory research. Dialecto combines both in every flashcard.",
      },
      {
        q: "Do I have to come up with the mnemonics myself?",
        a: "No. Dialecto creates a mnemonic with an illustration for every word automatically — you just review and let the method do the work.",
      },
      {
        q: "Which languages does it work with?",
        a: "All seven Dialecto languages: European Portuguese, Norwegian, English, German, Spanish, French, and Italian.",
      },
      {
        q: "Is Dialecto free?",
        a: "You can download Dialecto free on iOS and Android. Premium unlocks unlimited practice and starts with a 3-day free trial — cancel anytime.",
      },
    ],
    ctaTitle: "Stop forgetting vocabulary",
    ctaSubtitle: "Download Dialecto free and remember your first ten words with mnemonics today.",
  },
  "mn-pl": {
    slug: "mnemotechniki",
    lang: "pl",
    ogLocale: "pl_PL",
    alternates: MN_ALTERNATES,
    sideImg: "flashcards",
    title: "Mnemotechniki do nauki słówek — aplikacja, która pamięta za ciebie | Dialecto",
    description:
      "Dialecto to aplikacja z mnemotechnikami: każda fiszka ma obrazowy trik pamięciowy z ilustracją plus powtórki w odstępach. Przestań zapominać słówka. Za darmo na iOS i Androida.",
    eyebrow: "Metoda mnemotechniczna",
    h1: "Mnemotechniki: zapamiętaj słówka raz na zawsze",
    lead:
      "Zapominanie słówek jest normalne — uczenie się ich drugi raz już nie. W Dialecto każda fiszka dostaje mnemotechnikę: obrazowy trik pamięciowy z ilustracją, który łączy brzmienie słowa ze znaczeniem. A powtórki wracają dokładnie wtedy, gdy zaczynasz zapominać. Siedem języków, w tym norweski i portugalski europejski.",
    boardAlt: "Fiszka Dialecto z mnemotechniką — zapamiętaj słówka na zawsze",
    whyTitle: "Dlaczego mnemotechniki wygrywają z wkuwaniem",
    why: [
      {
        title: "Mózg kocha obrazy i historie",
        desc: "Słowo powiązane z wyrazistym obrazem albo mini-historią zostawia dużo silniejszy ślad pamięciowy niż powtórzone dwadzieścia razy. Mnemotechnika zamienia abstrakcyjne brzmienie w obraz, którego nie da się zapomnieć.",
      },
      {
        title: "Gotowa do każdego słówka",
        desc: "Nie musisz nic wymyślać: Dialecto tworzy dopasowaną mnemotechnikę z ilustracją do każdego słowa, które poznajesz — także tych z twoich własnych rozmów.",
      },
      {
        title: "Powtórki domykają sprawę",
        desc: "Mnemotechnika wprowadza słowo do pamięci, a powtórki w odstępach trzymają je tam na stałe. Fiszka wraca dokładnie przed momentem, w którym zacząłbyś zapominać.",
      },
    ],
    howTitle: "Jak działa nauka z mnemotechnikami w Dialecto",
    how: [
      "Poznajesz słowo w prawdziwej rozmowie — w scence, misji podróżnej albo tekście piosenki.",
      "Jedno tapnięcie tworzy fiszkę — Dialecto dokłada mnemotechnikę i obrazek łączący brzmienie ze znaczeniem.",
      "Powtarzasz w krótkich sesjach — powtórki w odstępach planują każdą fiszkę na idealny moment.",
      "Używasz słowa z powrotem w rozmowie — przypomnienie w realnych warunkach utrwala je na dobre.",
      "Działa we wszystkich siedmiu językach — od norweskiego po portugalski europejski.",
    ],
    faq: [
      {
        q: "Czym właściwie jest mnemotechnika?",
        a: "To trik pamięciowy, który łączy nowe słowo z czymś, co już znasz — obrazem, historyjką, podobnie brzmiącym wyrazem. Przykład: portugalskie 'a mala' (walizka) — wyobraź sobie koleżankę Malę ciągnącą przez lotnisko gigantyczną walizkę. Celowo absurdalne: im głupszy obraz, tym lepiej się trzyma.",
      },
      {
        q: "Czy mnemotechniki naprawdę działają?",
        a: "Tak — wyraziste skojarzenia i sprawdzanie się w rosnących odstępach czasu to dwie najlepiej udokumentowane techniki w badaniach nad pamięcią. Dialecto łączy obie w każdej fiszce.",
      },
      {
        q: "Czy muszę sam wymyślać mnemotechniki?",
        a: "Nie. Dialecto tworzy mnemotechnikę z ilustracją do każdego słowa automatycznie — ty tylko powtarzasz, a metoda robi swoje.",
      },
      {
        q: "Do jakich języków to działa?",
        a: "Do wszystkich siedmiu języków Dialecto: norweskiego, portugalskiego europejskiego, angielskiego, niemieckiego, hiszpańskiego, francuskiego i włoskiego.",
      },
      {
        q: "Ile kosztuje Dialecto?",
        a: "Aplikację pobierzesz za darmo na iOS i Androida. Premium odblokowuje nieograniczoną praktykę i zaczyna się od 3-dniowego darmowego okresu próbnego — możesz anulować w każdej chwili.",
      },
    ],
    ctaTitle: "Przestań zapominać słówka",
    ctaSubtitle: "Pobierz Dialecto za darmo i zapamiętaj dziś pierwsze dziesięć słówek z mnemotechnikami.",
  },
};

function useLandingSeo(page) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = page.title;

    // Trailing slash: Apache redirects the slash-less directory URL with a 301,
    // so the canonical must be the final (slashed) form.
    const url = `https://dialectoapp.com/${page.slug}/`;
    const managed = [];

    const setMeta = (selector, create, content) => {
      let el = document.head.querySelector(selector);
      let created = false;
      if (!el) {
        el = create();
        document.head.appendChild(el);
        created = true;
      }
      const prev = { el, created, content: el.getAttribute("content"), href: el.getAttribute("href") };
      managed.push(prev);
      if (content.href) el.setAttribute("href", content.href);
      if (content.content) el.setAttribute("content", content.content);
    };

    const meta = (name) => () => {
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      return m;
    };
    const prop = (p) => () => {
      const m = document.createElement("meta");
      m.setAttribute("property", p);
      return m;
    };

    setMeta('meta[name="description"]', meta("description"), { content: page.description });
    setMeta('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    }, { href: url });
    setMeta('meta[property="og:title"]', prop("og:title"), { content: page.title });
    setMeta('meta[property="og:description"]', prop("og:description"), { content: page.description });
    setMeta('meta[property="og:url"]', prop("og:url"), { content: url });
    if (page.ogLocale) {
      setMeta('meta[property="og:locale"]', prop("og:locale"), { content: page.ogLocale });
    }

    const prevLang = document.documentElement.getAttribute("lang");
    document.documentElement.setAttribute("lang", page.lang || "en");

    const alternateEls = (page.alternates || []).map((alt) => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", alt.hrefLang);
      l.setAttribute("href", alt.href);
      document.head.appendChild(l);
      return l;
    });

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "landing-faq-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    const appLd = document.createElement("script");
    appLd.type = "application/ld+json";
    appLd.id = "landing-app-ld";
    appLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      name: "Dialecto",
      operatingSystem: "iOS, Android",
      applicationCategory: "EducationalApplication",
      description: page.description,
      url,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    });
    document.head.appendChild(appLd);

    return () => {
      document.title = prevTitle;
      if (prevLang) document.documentElement.setAttribute("lang", prevLang);
      alternateEls.forEach((el) => el.remove());
      ld.remove();
      appLd.remove();
      managed.forEach(({ el, created, content, href }) => {
        if (created) {
          el.remove();
        } else {
          if (content !== null) el.setAttribute("content", content);
          if (href !== null) el.setAttribute("href", href);
        }
      });
    };
  }, [page]);
}

const AppleBadge = () => (
  <svg width="20" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);
const GoogleBadge = () => (
  <svg width="20" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zM14.892 13.1l2.302 2.302-12.41 7.105 10.108-9.407zM18.288 8.61l2.832 1.622a1 1 0 0 1 0 1.736l-2.832 1.622-2.49-2.49 2.49-2.49zM4.784 1.407l12.41 7.105-2.302 2.302L4.784 1.407z" />
  </svg>
);

const StoreButtons = () => (
  <div className="hero-cta-group landing-cta-group">
    <a href={STORE_APPLE} target="_blank" rel="noopener noreferrer" className="store-button apple" aria-label="Download on App Store">
      <AppleBadge />
      <span className="store-button-text">
        <span className="store-button-eyebrow">Download on the</span>
        <span className="store-button-name">App Store</span>
      </span>
    </a>
    <a href={STORE_GOOGLE} target="_blank" rel="noopener noreferrer" className="store-button google" aria-label="Get it on Google Play">
      <GoogleBadge />
      <span className="store-button-text">
        <span className="store-button-eyebrow">Get it on</span>
        <span className="store-button-name">Google Play</span>
      </span>
    </a>
  </div>
);

function LearnLanding({ page: pageKey }) {
  const page = PAGES[pageKey];
  const boardByLang = { pl: heroBoardPl, de: heroBoardDe };
  const boardImg = boardByLang[page.lang] || heroBoardEn;
  const UI = {
    en: {
      faqTitle: "Frequently asked questions",
      homeLink: "See everything Dialecto offers →",
      ctaNote: "Free download • 3-day Premium trial • Cancel anytime",
    },
    pl: {
      faqTitle: "Częste pytania",
      homeLink: "Zobacz wszystkie funkcje Dialecto →",
      ctaNote: "Darmowe pobranie • 3-dniowy okres próbny Premium • Anuluj w każdej chwili",
    },
    de: {
      faqTitle: "Häufige Fragen",
      homeLink: "Alle Funktionen von Dialecto ansehen →",
      ctaNote: "Kostenloser Download • 3 Tage Premium gratis • Jederzeit kündbar",
    },
    fr: {
      faqTitle: "Questions fréquentes",
      homeLink: "Découvrez tout ce que propose Dialecto →",
      ctaNote: "Téléchargement gratuit • Essai Premium de 3 jours • Annulable à tout moment",
    },
    it: {
      faqTitle: "Domande frequenti",
      homeLink: "Scopri tutto ciò che offre Dialecto →",
      ctaNote: "Download gratuito • Prova Premium di 3 giorni • Disdici quando vuoi",
    },
    es: {
      faqTitle: "Preguntas frecuentes",
      homeLink: "Descubre todo lo que ofrece Dialecto →",
      ctaNote: "Descarga gratis • Prueba Premium de 3 días • Cancela cuando quieras",
    },
    uk: {
      faqTitle: "Часті запитання",
      homeLink: "Дізнайтеся про всі можливості Dialecto →",
      ctaNote: "Безкоштовне завантаження • 3 дні Premium безкоштовно • Скасувати будь-коли",
    },
  };
  const ui = UI[page.lang] || UI.en;
  const faqTitle = ui.faqTitle;
  const homeLinkText = ui.homeLink;
  const ctaNote = ui.ctaNote;
  useLandingSeo(page);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageKey]);

  // Landing content is fixed per URL (each targets queries in one language),
  // so a header language switch navigates to the hreflang sibling instead of
  // re-rendering in place. Skips the initial mount: entering the page must
  // never redirect, only an explicit switch does.
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const lng = (i18n.language || "en").split("-")[0];
    if (lng === (page.lang || "en")) return;
    const alternates = page.alternates || [];
    const target =
      alternates.find((a) => a.hrefLang === lng) ||
      alternates.find((a) => a.hrefLang === "en");
    if (!target) return;
    const path = new URL(target.href).pathname.replace(/\/$/, "");
    if (path !== window.location.pathname.replace(/\/$/, "")) navigate(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className="landing">
      <section className="hero-section landing-hero">
        <div className="hero-background">
          <div className="hero-gradient-orb hero-orb-1"></div>
          <div className="hero-gradient-orb hero-orb-2"></div>
        </div>
        <div className="hero-content">
          <div className="hero-text">
            <span className="section-badge">{page.eyebrow}</span>
            <h1 className="hero-title landing-h1">{page.h1}</h1>
            <p className="hero-subtitle">{page.lead}</p>
            <StoreButtons />
          </div>
          <div className="hero-visual">
            <div className="hero-board">
              <img src={boardImg} alt={page.boardAlt} className="hero-board-img" />
              <div className="phone-glow"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="features-overview landing-section">
        <div className="section-header">
          <h2 className="section-title">{page.whyTitle}</h2>
        </div>
        <div className="features-grid landing-why-grid">
          {page.why.map((w, i) => (
            <div key={i} className="feature-card">
              <h3 className="feature-title">{w.title}</h3>
              <p className="feature-desc">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section landing-how">
        <div className="landing-how-inner">
          <div className="landing-how-text">
            <h2 className="section-title">{page.howTitle}</h2>
            <ul className="landing-how-list">
              {page.how.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
            <p className="landing-home-link">
              <Link to="/">{homeLinkText}</Link>
            </p>
          </div>
          <div className="landing-how-visual">
            <img
              src={page.sideImg === "flashcards" ? featureFlashcards : featureMissions}
              alt={
                page.sideImg === "flashcards"
                  ? "Dialecto flashcard with a mnemonic and picture"
                  : "Dialecto trip missions — practice conversations for your travel plans"
              }
              className="landing-side-img"
            />
          </div>
        </div>
      </section>

      <section className="faq-section landing-section">
        <div className="section-header">
          <h2 className="section-title">{faqTitle}</h2>
        </div>
        <div className="faq-container landing-faq">
          {page.faq.map((f, i) => (
            <div key={i} className="landing-faq-item">
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="final-cta">
        <div className="final-cta-content">
          <h2>{page.ctaTitle}</h2>
          <p>{page.ctaSubtitle}</p>
          <StoreButtons />
          <p className="cta-note">{ctaNote}</p>
        </div>
      </section>
    </div>
  );
}

export default LearnLanding;
